import React, { useEffect, useState } from 'react';
import { XIcon, AlertCircleIcon, CheckIcon, Loader2Icon } from 'lucide-react';
import { usePreventScroll } from '../../../hooks/usePreventScroll';
interface Props {
  isOpen: boolean;
  onClose: () => void;
  document: {
    id: string;
    name: string;
    type: string;
    summary: string | null;
  };
  onSave: (summary: string) => Promise<void>;
}
export const DocumentSummaryModal = ({
  isOpen,
  onClose,
  document,
  onSave
}: Props) => {
  // Add scroll prevention
  usePreventScroll(isOpen);
  const [isEditing, setIsEditing] = useState(false);
  const [summary, setSummary] = useState(document.summary || '');
  const [originalSummary] = useState(document.summary || '');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  useEffect(() => {
    if (isOpen) {
      setSummary(document.summary || '');
      setIsEditing(false);
      setError('');
      setShowSuccess(false);
    }
  }, [isOpen, document.summary]);
  const handleSave = async () => {
    if (summary.trim().length < 10) {
      setError('Summary must be at least 10 characters long');
      return;
    }
    try {
      setIsSaving(true);
      setError('');
      await onSave(summary.trim());
      setIsEditing(false);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
    } catch (err) {
      setError('Failed to save summary. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  const handleCancel = () => {
    if (isEditing) {
      setSummary(originalSummary);
      setIsEditing(false);
      setError('');
    } else {
      onClose();
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[200] flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && onClose()} data-id="element-1074">
      <div className="bg-surface-light border border-surface-border rounded-none w-full max-w-2xl animate-in fade-in-50 slide-in-from-bottom-4 sm:slide-in-from-bottom-0" onClick={e => e.stopPropagation()} data-id="element-1075">
        {/* Header */}
        <div className="px-6 py-4 border-b border-surface-border flex items-center justify-between" data-id="element-1076">
          <h2 className="text-lg font-medium text-white font-mono" data-id="element-1077">
            Document Summary
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-300 transition-colors" data-id="element-1078">
            <XIcon size={20} data-id="element-1079" />
          </button>
        </div>
        {/* Content */}
        <div className="p-6 space-y-4" data-id="element-1080">
          {/* Document Name */}
          <div className="bg-surface p-4 border border-surface-border rounded-none" data-id="element-1081">
            <h3 className="text-sm font-medium text-white font-mono mb-1" data-id="element-1082">
              {document.name}
            </h3>
            <p className="text-xs text-gray-400" data-id="element-1083">
              Type: {document.type.toUpperCase()}
            </p>
          </div>
          {/* Summary Section */}
          <div className="space-y-2" data-id="element-1084">
            <div className="flex items-center justify-between" data-id="element-1085">
              <label className="text-sm font-medium text-white font-mono" data-id="element-1086">
                Summary
              </label>
              {!isEditing && <button onClick={() => setIsEditing(true)} className="text-sm text-accent hover:text-accent-light transition-colors font-mono" data-id="element-1087">
                  Edit
                </button>}
            </div>
            {isEditing ? <textarea value={summary} onChange={e => setSummary(e.target.value)} className="w-full h-40 px-4 py-3 bg-surface border border-surface-border rounded-none 
                         text-white placeholder-gray-400 font-mono text-sm resize-none
                         focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent" placeholder="Enter a summary of the document's key points and important information..." data-id="element-1088" /> : <div className="bg-surface border border-surface-border rounded-none p-4 min-h-[160px]" data-id="element-1089">
                <p className="text-sm text-gray-100 whitespace-pre-wrap font-mono" data-id="element-1090">
                  {document.summary || 'No summary available.'}
                </p>
              </div>}
            {/* Error Message */}
            {error && <div className="flex items-center gap-2 text-status-error text-sm" data-id="element-1091">
                <AlertCircleIcon size={16} data-id="element-1092" />
                <span data-id="element-1093">{error}</span>
              </div>}
            {/* Success Message */}
            {showSuccess && <div className="flex items-center gap-2 text-green-400 text-sm" data-id="element-1094">
                <CheckIcon size={16} data-id="element-1095" />
                <span data-id="element-1096">Summary saved successfully</span>
              </div>}
          </div>
        </div>
        {/* Footer */}
        <div className="px-6 py-4 border-t border-surface-border flex justify-end gap-3" data-id="element-1097">
          <button onClick={handleCancel} className="px-4 py-2 text-sm text-gray-400 hover:text-gray-300 transition-colors font-mono" data-id="element-1098">
            {isEditing ? 'Cancel' : 'Close'}
          </button>
          {isEditing && <button onClick={handleSave} disabled={isSaving} className="px-6 py-2 bg-accent hover:bg-accent-light text-white text-sm 
                     font-mono uppercase tracking-wide rounded-none transition-colors
                     disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2" data-id="element-1099">
              {isSaving ? <>
                  <Loader2Icon size={16} className="animate-spin" data-id="element-1100" />
                  Saving...
                </> : 'Save Changes'}
            </button>}
        </div>
      </div>
    </div>;
};